import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function formatElapsed(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000))
  const hrs = Math.floor(totalSecs / 3600)
  const mins = Math.floor((totalSecs % 3600) / 60)
  const secs = totalSecs % 60
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

type BulkUploadElapsedTimerProps = {
  startedAt: number
  endedAt?: number | null
}

export const BulkUploadElapsedTimer: React.FC<BulkUploadElapsedTimerProps> = ({ startedAt, endedAt }) => {
  const [now, setNow] = useState(Date.now())
  const frozen = endedAt != null

  useEffect(() => {
    if (frozen) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [frozen])

  return <span className="font-mono tabular-nums">{formatElapsed((endedAt ?? now) - startedAt)}</span>
}

type BulkUploadProgressProps = {
  current: number
  total: number
  percent: number
  phase: 'uploading' | 'storing' | 'processing' | 'pipeline' | 'complete'
  startedAt?: number | null
  endedAt?: number | null
  currentDocumentNo?: string | null
  succeededDocs?: number
  failedDocs?: number
}

export const BulkUploadProgress: React.FC<BulkUploadProgressProps> = ({
  current,
  total,
  percent,
  phase,
  startedAt,
  endedAt,
  currentDocumentNo,
  succeededDocs = 0,
  failedDocs = 0,
}) => {
  const { t } = useTranslation(['BulkPodUpload'])

  const label =
    phase === 'uploading'
      ? t('BulkPodUpload:progress.uploading', 'Uploading files to storage…')
      : phase === 'storing'
        ? t('BulkPodUpload:progress.storing', 'Saving file list to server…')
        : phase === 'processing'
          ? t('BulkPodUpload:progress.processing', 'Processing queue on server…')
          : phase === 'pipeline'
            ? t('BulkPodUpload:progress.pipeline', 'Uploading & processing documents one at a time…')
            : t('BulkPodUpload:progress.complete', 'Processing complete')

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="inline-flex items-center gap-2 font-medium text-foreground">
          {phase !== 'complete' ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
          {label}
        </span>
        <div className="flex items-center gap-3 tabular-nums text-muted-foreground">
          {startedAt ? (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-0.5 text-xs font-medium text-foreground">
              <span>{t('BulkPodUpload:progress.elapsed', 'Elapsed')}</span>
              <BulkUploadElapsedTimer startedAt={startedAt} endedAt={endedAt} />
            </span>
          ) : null}
          {total > 0 ? (
            <span>
              {current} / {total} ({percent}%)
            </span>
          ) : null}
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-primary transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      {phase === 'pipeline' && currentDocumentNo ? (
        <p className="truncate text-xs text-muted-foreground">
          {t('BulkPodUpload:progress.currentDocument', 'Current document')}:{' '}
          <span className="font-mono font-medium text-foreground">{currentDocumentNo}</span>
        </p>
      ) : null}
      {phase === 'pipeline' && (succeededDocs > 0 || failedDocs > 0) ? (
        <p className="text-xs text-muted-foreground">
          {t('BulkPodUpload:progress.runStats', '{{ok}} succeeded · {{fail}} failed', {
            ok: succeededDocs,
            fail: failedDocs,
          })}
        </p>
      ) : null}
    </div>
  )
}

export default BulkUploadProgress
